import { z } from 'zod';
import { CASE_LINKS_MAX, DOCUMENT_MAX_FILES, caseLinkProblem, normalizeCaseLink } from '../document/document.js';
import { judicialPatchSchema, judicialRadicationSchema } from '../judicial/judicial.js';
/** General case lifecycle states. Driven by the workflow, never set ad hoc. */
export const CaseStatus = {
    RECEIVED: 'RECEIVED',
    PROCESSING: 'PROCESSING',
    PENDING_ASSIGNMENT: 'PENDING_ASSIGNMENT',
    IN_PROGRESS: 'IN_PROGRESS',
    WAITING_INFORMATION: 'WAITING_INFORMATION',
    WAITING_APPROVAL: 'WAITING_APPROVAL',
    WAITING_SIGNATURE: 'WAITING_SIGNATURE',
    TRANSFERRED: 'TRANSFERRED',
    ESCALATED: 'ESCALATED',
    RESOLVED: 'RESOLVED',
    CLOSED: 'CLOSED',
    ARCHIVED: 'ARCHIVED',
};
export const caseStatusSchema = z.enum(Object.values(CaseStatus));
export const CaseChannel = {
    WEB_PORTAL: 'WEB_PORTAL',
    EMAIL: 'EMAIL',
    PHYSICAL: 'PHYSICAL',
    API: 'API',
    INTERNAL: 'INTERNAL',
};
export const caseChannelSchema = z.enum(Object.values(CaseChannel));
export const Priority = { LOW: 'LOW', NORMAL: 'NORMAL', HIGH: 'HIGH', CRITICAL: 'CRITICAL' };
export const prioritySchema = z.enum(['LOW', 'NORMAL', 'HIGH', 'CRITICAL']);
/**
 * Qué significa cada prioridad. Es la definición con que el motor clasifica (opciones del esquema `CLASSIFICATION_V1`)
 * y la que se le muestra a la gente en «¿Qué significa?».
 */
export const PRIORITY_MEANING = { LOW: 'Puede esperar y no afecta a nadie', NORMAL: 'Es un trámite ordinario', HIGH: 'Afecta a un menor o a un adulto mayor, o el ciudadano ya lo había pedido antes', CRITICAL: 'Hay una urgencia médica, un riesgo de muerte o una tutela' };
export const CaseRelationKind = {
    DUPLICATE: 'duplicate',
    RELATED: 'related',
    FOLLOW_UP: 'follow-up',
    PARENT: 'parent',
    CHILD: 'child',
    APPEAL: 'appeal',
    TRANSFER: 'transfer',
};
export const citizenInputSchema = z.object({
    identificationType: z.string().max(20).optional(),
    identificationNumber: z.string().max(50).optional(),
    fullName: z.string().min(2).max(200),
    email: z.string().email().optional(),
    phone: z.string().max(40).optional(),
    address: z.string().max(300).optional(),
    /** Departamento y municipio de residencia en DIVIPOLA (2 y 5 dígitos; el municipio pertenece al departamento). */
    departmentCode: z.string().regex(/^\d{2}$/, 'Código DIVIPOLA de 2 dígitos').optional(),
    municipalityCode: z.string().regex(/^\d{5}$/, 'Código DIVIPOLA de 5 dígitos').optional(),
});
/** Corregir la ficha de un ciudadano: todo opcional; una cadena vacía borra el dato. */
export const citizenUpdateSchema = z.object({
    identificationType: z.string().max(20).nullable().optional(),
    identificationNumber: z.string().max(50).nullable().optional(),
    fullName: z.string().trim().min(2).max(200).optional(),
    email: z.union([z.string().trim().toLowerCase().email(), z.literal('')]).nullable().optional(),
    phone: z.string().max(40).nullable().optional(),
    /** Otros correos y teléfonos, además de los principales (que son a los que escribe y llama la entidad). */
    otherEmails: z.array(z.string().trim().toLowerCase().email('Correo inválido')).max(10).optional(),
    otherPhones: z.array(z.string().trim().min(1).max(40)).max(10).optional(),
    address: z.string().max(300).nullable().optional(),
    departmentCode: z.union([z.string().regex(/^\d{2}$/, 'Código DIVIPOLA de 2 dígitos'), z.literal('')]).nullable().optional(),
    municipalityCode: z.union([z.string().regex(/^\d{5}$/, 'Código DIVIPOLA de 5 dígitos'), z.literal('')]).nullable().optional(),
    /** Importante (Directorio): sus expedientes lo muestran en la bandeja, el archivo y la cabecera. */
    important: z.boolean().optional(),
});
/** Listado de ciudadanos: búsqueda por nombre, documento o correo; orden y cursor. */
export const citizenListSchema = z.object({
    q: z.string().trim().max(120).optional(),
    sort: z.enum(['fullName', 'createdAt', 'caseCount']).default('fullName'),
    dir: z.enum(['asc', 'desc']).default('asc'),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/**
 * Dónde viene la petición (cases.metadata.petitionSource): `body` = en la descripción escrita al radicar;
 * `document` = en un archivo adjunto marcado como petición (una carta, un oficio, un formulario escaneado), con la
 * descripción vacía o como complemento. El motor de decisión lee el texto de esos archivos como parte de la petición.
 */
export const PetitionSource = { BODY: 'body', DOCUMENT: 'document' };
export const petitionSourceSchema = z.enum(['body', 'document']);
export function petitionSourceOf(metadata) {
    const m = (metadata ?? {});
    return m.petitionSource === 'document' ? 'document' : 'body';
}
/** Un enlace a un anexo (ver `case_links`): http/https completo; sin esquema se completa con https://. */
export const caseLinkUrlSchema = z.string().trim().min(1, 'Escribe el enlace').transform(normalizeCaseLink).superRefine((v, ctx) => {
    const problem = caseLinkProblem(v);
    if (problem)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
});
/** Anexos por enlace de una radicación: se listan con los adjuntos y la constancia los deja escritos. */
export const caseLinksSchema = z.array(caseLinkUrlSchema).max(CASE_LINKS_MAX, `Máximo ${CASE_LINKS_MAX} enlaces`).default([]);
/** POST /documents/cases/:caseId/links: enlaces que se agregan a un expediente ya radicado. */
export const addCaseLinksSchema = z.object({ urls: z.array(caseLinkUrlSchema).min(1).max(CASE_LINKS_MAX, `Máximo ${CASE_LINKS_MAX} enlaces`) });
/** Archivos que una radicación puede anunciar: la petición en archivo más los anexos. */
export const INTAKE_MAX_DOCUMENTS = DOCUMENT_MAX_FILES + 1;
/** Caracteres de los archivos de la petición que entran al texto que clasifica el motor. */
export const PETITION_TEXT_MAX = 20_000;
/** Campos de la radicación sin la regla cruzada (para armar formularios campo a campo). */
export const createCaseFields = z.object({
    /**
     * Tipo declarado. Si se omite (portal ciudadano), el caso entra con el tipo provisional
     * (`intake.provisionalType`) y el motor de decisión define el tipo definitivo.
     */
    caseTypeCode: z.string().min(1).max(50).optional(),
    channel: caseChannelSchema,
    subject: z.string().min(3).max(300),
    /** Obligatoria salvo que la petición venga en un archivo (`petitionSource: 'document'`). */
    body: z.string().max(50_000).default(''),
    /** `document`: la petición viene en un archivo que se sube después de radicar, marcado como petición. */
    petitionSource: petitionSourceSchema.default('body'),
    /**
     * Archivos que se subirán enseguida (la petición en archivo y los anexos). La clasificación espera a que lleguen y a
     * que su texto esté extraído, o a que el canal avise que terminó (`POST …/intake/complete`), o dos minutos.
     */
    expectedDocuments: z.number().int().min(0).max(INTAKE_MAX_DOCUMENTS).default(0),
    /** Anexos por enlace (lo que pesa más de lo permitido o ya está en una nube): no se descargan ni entran al índice. */
    links: caseLinksSchema,
    citizen: citizenInputSchema.optional(),
    anonymous: z.boolean().default(false),
    /** Solicitud anónima: el correo al que llega la respuesta (obligatorio). No crea ficha de ciudadano: queda en el expediente. */
    contactEmail: z.string().trim().toLowerCase().email('Correo inválido').max(200).optional(),
    metadata: z.record(z.unknown()).optional(),
    /**
     * Lo llenado en el formulario de ingreso del tipo elegido (`caseTypes.intakeFormCode`), por nombre de campo. El API
     * lo valida contra la definición del formulario (obligatorios, tipos, opciones, `showWhen`) y lo guarda en
     * `cases.metadata.formData`. Solo cuenta cuando la persona declaró el tipo; con tipo provisional se ignora.
     */
    formData: z.record(z.unknown()).optional(),
    /** Requerimiento judicial radicado a mano: el despacho que escribe y su radicado (el accionante es `citizen`). */
    courtId: z.string().uuid().optional(),
    judicialRadication: judicialRadicationSchema.optional(),
    /** Idempotency key so duplicate submissions (email retries, double clicks) do not create two cases. */
    idempotencyKey: z.string().max(200).optional(),
});
/** La petición está en la descripción o en un archivo que viene en camino; nunca en ninguno de los dos. */
const petitionPresent = (v, ctx) => {
    if (v.petitionSource === 'document') {
        if (v.expectedDocuments < 1)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['expectedDocuments'], message: 'Anuncia el archivo que contiene la petición' });
    }
    else if (!v.body.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['body'], message: 'Escribe la petición o adjunta el archivo que la contiene' });
    }
};
/** Una solicitud anónima, desde cualquier canal, deja el correo al que llega la respuesta. */
const anonymousContact = (v, ctx) => {
    if (v.anonymous && !v.contactEmail)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['contactEmail'], message: 'Escribe el correo al que llegará la respuesta' });
};
/**
 * Radicación interna («Nueva solicitud») con peticionario: departamento, municipio y dirección son obligatorios. Los
 * demás canales (portal, correo, integraciones) radican con lo que traigan. En todos, el municipio es del departamento.
 */
const citizenPlace = (v, ctx) => {
    const c = v.citizen;
    if (c?.departmentCode && c.municipalityCode && !c.municipalityCode.startsWith(c.departmentCode))
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['citizen', 'municipalityCode'], message: 'El municipio no es de ese departamento' });
    if (v.channel !== 'INTERNAL' || v.anonymous)
        return;
    if (!c?.departmentCode)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['citizen', 'departmentCode'], message: 'Elige el departamento' });
    if (!c?.municipalityCode)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['citizen', 'municipalityCode'], message: 'Elige el municipio' });
    if (!c?.address?.trim())
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['citizen', 'address'], message: 'Escribe la dirección' });
};
/** POST /api/v1/cases body. Fast path: create, radicate, enqueue. */
export const createCaseSchema = createCaseFields.superRefine((v, ctx) => { petitionPresent(v, ctx); citizenPlace(v, ctx); anonymousContact(v, ctx); });
/**
 * POST /api/v1/cases/petition-preview.pdf: la constancia de radicación tal como va quedando mientras se llena «Nueva
 * solicitud». Todo es opcional (el formulario está a medio llenar) y no se guarda nada.
 */
export const petitionPreviewSchema = z.object({
    caseTypeCode: z.string().max(50).optional(),
    subject: z.string().max(300).default(''),
    body: z.string().max(50_000).default(''),
    petitionSource: petitionSourceSchema.default('body'),
    /** Los enlaces escritos hasta ahora; los que aún no son válidos no se muestran. */
    links: z.array(z.string().max(2000)).max(50).default([]),
    anonymous: z.boolean().default(false),
    contactEmail: z.string().max(200).optional(),
    citizen: z.object({
        fullName: z.string().max(200).optional(),
        identificationType: z.string().max(20).optional(),
        identificationNumber: z.string().max(50).optional(),
        email: z.string().max(200).optional(),
        phone: z.string().max(40).optional(),
        address: z.string().max(300).optional(),
        municipalityCode: z.string().max(5).optional(),
    }).optional(),
});
/**
 * Dónde va el sello de radicación sobre la primera página del archivo con la petición («Nueva solicitud»): siempre
 * arriba, a la izquierda, al centro o a la derecha. Se elige al radicar (`?stamp=` al subir la petición).
 */
export const PETITION_STAMP_POSITIONS = ['TOP_LEFT', 'TOP_CENTER', 'TOP_RIGHT'];
export const petitionStampPositionSchema = z.enum(PETITION_STAMP_POSITIONS);
export const PETITION_STAMP_POSITION_LABEL = { TOP_LEFT: 'Arriba a la izquierda', TOP_CENTER: 'Arriba al centro', TOP_RIGHT: 'Arriba a la derecha' };
/**
 * Campo `payload` (JSON) de POST /api/v1/cases/petition-stamp-preview.pdf (multipart, con el archivo de la petición):
 * la primera página sellada tal como quedaría, con el radicado de ejemplo. No se guarda nada.
 */
export const petitionStampPreviewSchema = z.object({
    position: petitionStampPositionSchema.default('TOP_RIGHT'),
    caseTypeCode: z.string().max(50).optional(),
    anonymous: z.boolean().default(false),
    contactEmail: z.string().max(200).optional(),
    fullName: z.string().max(200).optional(),
    identificationType: z.string().max(20).optional(),
    identificationNumber: z.string().max(50).optional(),
});
/** Máximo de caracteres de la descripción que escribe el ciudadano en el portal (los demás canales usan el del esquema general). */
export const PORTAL_BODY_MAX = 1200;
/** POST /api/v1/public/cases body: el del portal ciudadano, con la descripción acotada. */
export const publicCreateCaseSchema = createCaseFields.extend({ body: z.string().max(PORTAL_BODY_MAX).default('') }).superRefine((v, ctx) => { petitionPresent(v, ctx); anonymousContact(v, ctx); });
/** Máximo del texto leído del archivo con la solicitud que el portal reenvía para sugerir el tipo (`documentText`). */
export const PORTAL_DOCUMENT_TEXT_MAX = 8000;
/** Campos de texto de `POST /api/v1/public/cases/classify/document` (multipart, junto al archivo con la solicitud). */
export const publicClassifyDocumentFieldsSchema = z.object({ subject: z.string().max(300).default(''), body: z.string().max(PORTAL_BODY_MAX).default('') });
/**
 * POST /api/v1/public/cases/classify body: lo que el ciudadano lleva escrito y, si adjuntó el archivo con la solicitud,
 * el texto que se leyó de él (`…/classify/document` lo devuelve), para sugerirle el tipo antes de radicar.
 */
export const publicClassifyCaseSchema = publicClassifyDocumentFieldsSchema
    .extend({ documentText: z.string().max(PORTAL_DOCUMENT_TEXT_MAX).optional() })
    .refine((v) => v.body.trim() || v.documentText?.trim(), { path: ['body'], message: 'Escribe la solicitud o adjunta el archivo que la contiene' });
export function caseTypeSourceOf(metadata) {
    const m = (metadata ?? {});
    return m.typeSource ?? (m.typeDeclared === false ? 'provisional' : 'citizen');
}
export const updateCaseSchema = z.object({
    subject: z.string().min(3).max(300).optional(),
    priority: prioritySchema.optional(),
    specialtyId: z.string().uuid().nullable().optional(),
    organizationalUnitId: z.string().uuid().nullable().optional(),
}).merge(judicialPatchSchema);
export const listCasesQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    status: z.array(caseStatusSchema).or(caseStatusSchema).optional(),
    channel: caseChannelSchema.optional(),
    priority: prioritySchema.optional(),
    assigneeId: z.string().uuid().optional(),
    specialtyId: z.string().uuid().optional(),
    organizationalUnitId: z.string().uuid().optional(),
    overdue: z.coerce.boolean().optional(),
    /** Término del expediente que vence hoy (sin contar lo ya vencido). */
    dueToday: z.coerce.boolean().optional(),
    /** Término del expediente que vence de hoy a tres días hábiles. */
    dueSoon: z.coerce.boolean().optional(),
    escalated: z.coerce.boolean().optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    workflowCode: z.string().optional(),
    caseTypeCode: z.string().max(50).optional(),
    /** Requerimientos judiciales: los de un despacho. */
    courtId: z.string().uuid().optional(),
    requiresReview: z.coerce.boolean().optional(),
    mine: z.coerce.boolean().optional(),
    /** Solo los radicados que la persona marcó con la estrella. */
    favorites: z.coerce.boolean().optional(),
    /** Solo las peticiones de alguien marcado como importante en el Directorio (no las anónimas). */
    important: z.coerce.boolean().optional(),
    q: z.string().max(200).optional(),
    sort: z.enum(['createdAt', 'dueAt', 'priority', 'radication']).default('createdAt'),
    dir: z.enum(['asc', 'desc']).default('desc'),
});
//# sourceMappingURL=case.js.map